<template>
    <div>
        <div class="mb-8">
            <strong class="block text-slate-900 text-base leading-6 font-semibold mb-2"> Campaign Name </strong>
            <div v-if="!editing" class="flex gap-2">
                {{ campaignName }}
                <vx-button
                    @click="editing = true"
                    type="button"
                    size="xs"
                    color="muted"
                    data-test="launch-preview-edit-name"
                >
                    Edit
                </vx-button>
            </div>
            <div v-else class="flex gap-2 [&>div>input]:h-12">
                <vx-input
                    :maxlength="84"
                    v-model="campaignName"
                    size="lg"
                    @input="emit('update:name', campaignName)"
                ></vx-input>
                <vx-button
                    @click="saveName()"
                    type="button"
                    size="lg"
                    color="primary"
                    data-test="launch-preview-edit-name-save"
                >
                    Save
                    <font-awesome-icon widthAuto :icon="faCheck"></font-awesome-icon>
                </vx-button>
            </div>
        </div>

        <div v-if="showEstimatedCost" class="mb-8 relative">
            <perforation-edge position="top" />

            <div
                class="px-8 py-10 bg-white rounded-xs border border-white shadow-[0_1px_3px_0_rgba(5,71,107,0.15),_0_1px_2px_0_rgba(5,71,107,0.07)]"
            >
                <div class="flex flex-col gap-2">
                    <div class="flex gap-2 justify-between items-center">
                        <span class="text-base font-bold">Estimations</span>
                        <vx-button
                            @click.stop="estimationModal = true"
                            type="button"
                            color="muted-light"
                            size="xs"
                            data-test="estimation-info-btn"
                        >
                            <font-awesome-icon
                                widthAuto
                                :icon="faCircleQuestion"
                                size="lg"
                                class="text-black"
                            ></font-awesome-icon>
                        </vx-button>
                    </div>

                    <p v-if="showEstimatedCost" class="text-sm text-slate-500" data-test="cost-description">
                        This is an estimate based on the current number of contacts in your chosen audience segment.
                        Final costs may vary if the number of eligible contacts changes before sending.
                    </p>
                </div>

                <div class="mt-4 pl-6 flex flex-col gap-2">
                    <div v-if="currentOrgUnit?.id" class="flex items-baseline self-stretch gap-1">
                        <span class="text-base">Groups</span>
                        <div
                            class="h-1 flex flex-1 justify-center items-center border-b border-slate-400 border-dashed"
                        ></div>
                        <span class="text-base">{{ formatCount(props.groupIds?.length ?? 0) }}</span>
                    </div>
                    <div class="flex items-baseline self-stretch gap-1">
                        <span class="text-base">Audience Size</span>
                        <div
                            class="h-1 flex flex-1 justify-center items-center border-b border-slate-400 border-dashed"
                        ></div>
                        <span class="text-base">{{ formatCount(props.estimation) }}</span>
                    </div>
                    <div class="flex items-baseline self-stretch gap-1">
                        <span class="text-base">Estimated Cost</span>
                        <div
                            class="h-1 flex flex-1 justify-center items-center border-b border-slate-400 border-dashed"
                        ></div>
                        <span class="text-base">{{ currencyCents(props.estimatedCost) }}</span>
                    </div>
                </div>
            </div>

            <perforation-edge position="bottom" />
        </div>
        <div v-else class="mb-8">
            <strong class="block text-slate-900 text-base leading-6 font-semibold mb-2"> Audience Size </strong>
            <div class="flex items-center gap-2">
                <div v-if="props.groupIds?.length">
                    {{ pluralized('group', props.groupIds.length, true) }}
                </div>
                <font-awesome-icon widthAuto v-if="props.groupIds?.length" :icon="faArrowRight"></font-awesome-icon>
                <div>
                    <span>{{ formatCount(props.estimation) }} Recipients</span>
                    <span
                        @click.stop="estimationModal = true"
                        class="text-xs font-normal cursor-pointer"
                        data-test="estimation-info-btn"
                    >
                        (est
                        <font-awesome-icon widthAuto :icon="faCircleInfo"></font-awesome-icon>
                        )
                    </span>
                </div>
            </div>
        </div>

        <div>
            <strong class="block text-slate-900 text-base leading-6 font-semibold mb-2"> Message Preview </strong>
            <div
                class="p-4 whitespace-pre-line bg-white shadow text-slate-700 text-sm font-normal rounded-[16px] rounded-bl"
            >
                {{ props.bodyMessage }}
            </div>
            <div
                v-if="props.mediaUrl"
                class="mt-2 p-4 bg-white shadow rounded-[16px] rounded-bl max-w-[208px] max-h-[500px]"
            >
                <img :src="props.mediaUrl" class="w-full block object-cover rounded rounded-b-sm" />
            </div>
        </div>
    </div>

    <vx-modal v-model:visible="estimationModal" padding="standard" size="md" data-test="estimation-modal">
        <template v-slot:header>
            {{ showEstimatedCost ? 'Estimated Cost' : 'Recipient Estimation' }}
        </template>
        <div v-if="showEstimatedCost" class="text-slate-500 text-base flex flex-col gap-4">
            <p>This is only an estimate, as the audience segment can vary in size prior to sending.</p>
            <div>
                <p>Estimated Costs are calculated using:</p>
                <ul class="pl-6 list-disc">
                    <li>Message Type: SMS or MMS</li>
                    <li>Quantity of SMS Segments (message length)</li>
                    <li>Contracted rates (SMS or MMS rate + Carrier Fee)</li>
                    <li>Quantity of contacts within the audience at time of estimation</li>
                </ul>
            </div>
            <p>
                Messages that contain variables can also cause the estimate to diverge from the actual cost at send
                time.
            </p>
        </div>
        <div v-else class="text-slate-500 text-base flex flex-col gap-4">
            <p>
                The total number of recipients is based on several variables that can change all the way up to the time
                a message sends. As a result, we can only provide an exact number once messages go out.
            </p>
            <p>
                However, we do our best to provide an accurate estimation so you have a general idea of how many people
                you'll reach.
            </p>
        </div>
    </vx-modal>
</template>

<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxInput, VxButton, VxModal } from '@voxie/frontend-components';
import PerforationEdge from '~/components/general/PerforationEdge.vue';
import { computed, ref, onMounted, inject } from 'vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { teamsClient } from '~/services';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { faCheck, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { formatCount, pluralized } from '../filters';
import { currencyCents } from '~/utils/helpers';

const FEATURES = inject('FEATURES');

const emit = defineEmits(['save:name']);

const props = defineProps({
    mediaUrl: {
        type: String,
        required: false,
    },
    bodyMessage: {
        type: String,
        required: true,
    },
    estimation: {
        required: false,
    },
    estimatedCost: {
        required: false,
    },
    groupIds: {
        type: Array,
    },
});

const campaignName = defineModel('name', {
    type: String,
    required: true,
});

const editing = ref(false);
const estimationModal = ref(false);

const saveName = () => {
    editing.value = false;
    emit('save:name');
};

const teamId = Spark.state.currentTeam.id;
const { onCorporate, currentGroup, currentOrgUnit } = useCurrentGroup();

const costVisibility = ref({ org_units: false, groups: false });

const showEstimatedCost = computed(
    () =>
        FEATURES.new_billing_implementation &&
        props.estimatedCost !== null &&
        (onCorporate() ||
            (currentGroup.value?.group_id && costVisibility.value.groups) ||
            (currentOrgUnit.value?.id && costVisibility.value.org_units))
);

onMounted(async () => {
    if (!onCorporate() && FEATURES.new_billing_implementation) {
        try {
            const response = await teamsClient.getCostVisibility(teamId);
            costVisibility.value = response.data;
        } catch (e) {
            console.error(e);
        }
    }
});
</script>
